import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVeEquipa } from '@/features/auth/AuthContext';
import { useSettings } from '@/features/app/hooks';
import { useCreateObra, useObras, useUpdateObra } from '@/features/obras/hooks';
import type { Obra } from '@/types';
import { formatHoras, monthOf, todayISO } from '@/lib/format';
import { AppLayout } from '@/components/AppLayout';
import { ObraSheet } from '@/components/sheets/ObraSheet';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Icon } from '@/components/ui/Icon';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';

/** The obra catalogue — searchable, with this month's hours in the caller's scope. */
export function ObrasPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const veEquipa = useVeEquipa();
  const settings = useSettings();
  const mes = monthOf(todayISO());

  const [busca, setBusca] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editar, setEditar] = useState<Obra | null>(null);

  const obras = useObras(mes, veEquipa);
  const criar = useCreateObra();
  const alterar = useUpdateObra();

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const todas = obras.data ?? [];
    if (!termo) return todas;
    return todas.filter(
      (o) =>
        o.nome.toLowerCase().includes(termo) ||
        (o.centroCusto ?? '').toLowerCase().includes(termo),
    );
  }, [busca, obras.data]);

  const guardar = async (input: { nome: string; centroCusto: string | null }) => {
    try {
      if (editar) {
        await alterar.mutateAsync({ id: editar.id, ...input });
        toast.show(t('users.atualizado'));
      } else {
        await criar.mutateAsync(input);
        toast.show(t('sheet.obraCriada', { nome: input.nome }));
      }
      setSheetOpen(false);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const mostrarCC = settings.data?.mostrarCentroCusto ?? true;

  return (
    <AppLayout
      title={t('header.obras')}
      subtitle={t('header.obrasAtivas', { count: (obras.data ?? []).filter((o) => !o.arquivada).length })}
    >
      <div className="flex flex-col gap-4">
        <div className="relative md:max-w-md">
          <Icon
            name="search"
            className="pointer-events-none absolute left-3.5 top-3.5 text-[19px] text-conduril-muted"
          />
          <input
            className="field pl-10 text-[13px]"
            placeholder={t('obras.buscaPlaceholder')}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="label-eyebrow">{t('obras.base')}</div>
          <div className="num text-[11px] text-conduril-muted">
            {t('obras.contagem', { filtradas: filtradas.length, total: (obras.data ?? []).length })}
          </div>
        </div>

        {obras.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {obras.error && <ErrorBanner error={obras.error} onRetry={() => void obras.refetch()} />}

        {obras.data && filtradas.length === 0 && <EmptyState message={t('obras.vazio')} />}

        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {filtradas.map((obra) => (
            <li key={obra.id}>
              <button
                type="button"
                disabled={!veEquipa}
                onClick={() => {
                  setEditar(obra);
                  setSheetOpen(true);
                }}
                className="flex w-full items-center justify-between gap-3 rounded-2xl border border-conduril-border bg-white p-3.5 text-left shadow-card disabled:cursor-default"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-[14px] font-semibold leading-tight text-conduril-ink">
                      {obra.nome}
                    </span>
                    {obra.arquivada && (
                      <span className="shrink-0 rounded-full bg-[#f0f1f6] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-conduril-muted">
                        {t('obras.arquivada')}
                      </span>
                    )}
                  </span>
                  {mostrarCC && obra.centroCusto && (
                    <span className="num mt-1.5 block text-[10px] tracking-wide text-conduril-muted">
                      {obra.centroCusto}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-right">
                  <span className="num block text-[14px] font-semibold text-conduril">
                    {formatHoras(obra.horasNoMes)}
                  </span>
                  <span className="mt-1 block text-[10px] text-conduril-muted">
                    {veEquipa ? t('obras.equipaMes') : t('obras.esteMes')}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>

        {veEquipa && (
          <button
            type="button"
            onClick={() => {
              setEditar(null);
              setSheetOpen(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-[#b9c0d2] bg-white px-4 py-3.5 text-sm font-semibold text-conduril transition hover:border-conduril md:w-auto md:self-start md:px-6"
          >
            <Icon name="add_business" className="text-[19px]" /> {t('obras.criar')}
          </button>
        )}
      </div>

      <ObraSheet
        open={sheetOpen}
        obra={editar}
        busy={criar.isPending || alterar.isPending}
        onClose={() => setSheetOpen(false)}
        onSave={(input) => void guardar(input)}
      />
    </AppLayout>
  );
}
